import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, FileText, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { SEO, getLocalBusinessSchema } from "../components/SEO";
import ServiceRequestBuilder from "../components/tools/ServiceRequestBuilder";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [activeTab, setActiveTab] = useState("service-request");
  const [showServiceRequest, setShowServiceRequest] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: MapPin,
      title: "Address",
      details: ["Madison, Mississippi, USA", "Kingston, Jamaica"],
    },
    {
      icon: Phone,
      title: "Phone",
      details: ["+1 (876) 833-3918", "+1 (954) 830-1186"],
    },
    {
      icon: Mail,
      title: "Email",
      details: ["info@comply-cms.com", "support@comply-cms.com"],
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: ["Monday - Friday: 9AM - 5PM", "Saturday: 9AM - 1PM"],
    },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact CompLy CMS | Request Compliance & Training Support"
        description="Contact CompLy CMS for compliance consulting, certification support, and training inquiries. Phone: +1 (876) 833-3918. Offices in Madison, Mississippi (USA) and Kingston, Jamaica. Serving the United States, the Caribbean & internationally."
        url="/contact"
        keywords={[
          "contact CompLy CMS",
          "compliance consulting Jamaica",
          "ISO consultant Mississippi",
          "ISO training Madison MS",
          "ISO certification support",
          "Caribbean training contact",
          "corporate training quote"
        ]}
        structuredData={getLocalBusinessSchema()}
      />

      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-br from-navy-darker via-navy-dark to-navy">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-brand/20 text-brand-light px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileText className="h-4 w-4" />
              Request Support
            </span>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white tracking-tight mb-6" data-testid="contact-title">
              Let&apos;s Build Your Compliance Journey
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Whether you need consulting, training, or certification support, our team is ready to help. 
              Use our guided request builder for the fastest response.
            </p>
          </div>
        </div>
      </section>

      {/* Service Request Modal */}
      {showServiceRequest && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <ServiceRequestBuilder onClose={() => setShowServiceRequest(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1">
              <h2 className="font-heading font-bold text-2xl mb-6">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-brand" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>
                      {item.details.map((detail, i) => (
                        <p key={i} className="text-sm text-muted-foreground">{detail}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick CTA */}
              <div className="mt-8 p-6 bg-brand/5 border border-brand/20 rounded-xl">
                <h3 className="font-semibold mb-2">Need a Quick Response?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Use our Service Request Builder for a structured submission that helps us respond faster.
                </p>
                <Button 
                  onClick={() => setShowServiceRequest(true)}
                  className="w-full bg-brand hover:bg-brand-dark"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Open Request Builder
                </Button>
              </div>
            </div>

            {/* Contact Forms */}
            <div className="lg:col-span-2">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="service-request" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Service Request
                  </TabsTrigger>
                  <TabsTrigger value="general" className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    General Inquiry
                  </TabsTrigger>
                </TabsList>

                {/* Service Request Tab */}
                <TabsContent value="service-request">
                  <Card>
                    <CardContent className="p-6 lg:p-8">
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FileText className="h-8 w-8 text-brand" />
                        </div>
                        <h2 className="font-heading font-bold text-2xl mb-3">
                          Service Request Builder
                        </h2>
                        <p className="text-muted-foreground max-w-md mx-auto mb-6">
                          Our guided request builder helps you provide the information we need to give you 
                          accurate recommendations and faster responses.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4 mb-6">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Consulting & Implementation
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Training Programs
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Audit Services
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Certification Support
                          </div>
                        </div>
                        <Button 
                          size="lg"
                          onClick={() => setShowServiceRequest(true)}
                          className="bg-brand hover:bg-brand-dark"
                        >
                          Start Service Request
                          <Send className="ml-2 h-4 w-4" />
                        </Button>
                        <p className="text-xs text-muted-foreground mt-4">
                          Takes about 5 minutes • Response within 1-2 business days
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* General Inquiry Tab */}
                <TabsContent value="general">
                  <Card>
                    <CardContent className="p-6 lg:p-8">
                      {submitted ? (
                        <div className="text-center py-8">
                          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                          </div>
                          <h2 className="font-heading font-bold text-2xl mb-4">Message Sent!</h2>
                          <p className="text-muted-foreground mb-6">
                            Thank you for contacting us. We&apos;ll get back to you within 24 hours.
                          </p>
                          <Button onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", subject: "", message: "" }); }}>
                            Send Another Message
                          </Button>
                        </div>
                      ) : (
                        <>
                          <h2 className="font-heading font-bold text-2xl mb-6">Send us a Message</h2>
                          <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <Label htmlFor="name">Full Name *</Label>
                                <Input
                                  id="name"
                                  name="name"
                                  value={formData.name}
                                  onChange={handleInputChange}
                                  placeholder="Your name"
                                  className={errors.name ? "border-destructive" : ""}
                                  data-testid="contact-name"
                                />
                                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="email">Email Address *</Label>
                                <Input
                                  id="email"
                                  name="email"
                                  type="email"
                                  value={formData.email}
                                  onChange={handleInputChange}
                                  placeholder="your.email@example.com"
                                  className={errors.email ? "border-destructive" : ""}
                                  data-testid="contact-email"
                                />
                                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                              </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number</Label>
                                <Input
                                  id="phone"
                                  name="phone"
                                  type="tel"
                                  value={formData.phone}
                                  onChange={handleInputChange}
                                  placeholder="+1 (876) 123-4567"
                                  data-testid="contact-phone"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="subject">Subject *</Label>
                                <Input
                                  id="subject"
                                  name="subject"
                                  value={formData.subject}
                                  onChange={handleInputChange}
                                  placeholder="How can we help?"
                                  className={errors.subject ? "border-destructive" : ""}
                                  data-testid="contact-subject"
                                />
                                {errors.subject && <p className="text-sm text-destructive">{errors.subject}</p>}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="message">Message *</Label>
                              <Textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                placeholder="Tell us more about your inquiry..."
                                rows={6}
                                className={errors.message ? "border-destructive" : ""}
                                data-testid="contact-message"
                              />
                              {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
                            </div>

                            <Button
                              type="submit"
                              className="w-full md:w-auto bg-brand hover:bg-brand-dark text-white px-8"
                              disabled={submitting}
                              data-testid="contact-submit"
                            >
                              {submitting ? "Sending..." : (
                                <>
                                  Send Message
                                  <Send className="ml-2 h-4 w-4" />
                                </>
                              )}
                            </Button>
                          </form>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
